import { sanitizeHtml } from "./sanitizer";
import { I18n } from "@/i18n/i18n";
import type { DiffTables } from "./diff/text";
import type { DiffStyle, PythiaDiffLine } from "@/types/FeedbackResult";

/* Removes newlines and unwraps <li>, <ins>, <del>, <span> wrappers so the
 * raw text content can be measured for line-length thresholds. */
export function stripOuterHtml(html: string): string {
    return html
        .replace(/\n/g, "")
        .replace(/<li[^>]*>(.*)<\/li>/gs, "$1")
        .replace(/<ins>(.*)<\/ins>/gs, "$1")
        .replace(/<del>(.*)<\/del>/gs, "$1")
        .replace(/<span>(.*)<\/span>/gs, "$1");
}

export function pythiaDiffStyle(diff: PythiaDiffLine[]): DiffStyle {
    for (const diffLine of diff) {
        const [, , insHtml, delHtml] = diffLine;
        if (insHtml !== null && stripOuterHtml(insHtml).length >= 55) return "unified";
        if (delHtml !== null && stripOuterHtml(delHtml).length >= 55) return "unified";
    }
    return "split";
}

/**
 * Builds unified and split diff tables from the pre-baked Pythia diff array.
 * Unlike text/CSV diffs, Pythia supplies pre-computed HTML per line; this
 * function strips wrappers, sanitizes, and lays the lines into table rows.
 */
export function computePythiaDiff(diff: PythiaDiffLine[]): DiffTables {
    const yourOutput = I18n.t("js.submissions_show.your_output");
    const expectedLabel = I18n.t("js.submissions_show.expected");
    const lineNumber = I18n.t("js.submissions_show.line_number");
    const outputLabel = I18n.t("js.submissions_show.output");

    // Strip and sanitize each line once for both the unified and split passes.
    const processed = diff.map(([expNr, genNr, insHtml, delHtml, isUnchanged]) => ({
        expNr,
        genNr,
        ins: insHtml !== null ? sanitizeHtml(stripOuterHtml(insHtml)) : null,
        del: delHtml !== null ? sanitizeHtml(stripOuterHtml(delHtml)) : null,
        isUnchanged,
    }));

    // -------------------------------------------------------------------------
    // Unified table
    // -------------------------------------------------------------------------
    let unifiedRows = "";
    for (const { expNr, genNr, ins, del, isUnchanged } of processed) {
        if (!isUnchanged && del !== null) {
            unifiedRows += `<tr>
<td class="line-nr">${genNr}</td>
<td class="line-nr"></td>
<td class="del">${del}</td>
</tr>`;
        }
        if (!isUnchanged && ins !== null) {
            unifiedRows += `<tr>
<td class="line-nr"></td>
<td class="line-nr">${expNr}</td>
<td class="ins">${ins}</td>
</tr>`;
        }
        if (isUnchanged) {
            const content = ins ?? "";
            unifiedRows += `<tr>
<td class="line-nr">${genNr}</td>
<td class="line-nr">${expNr}</td>
<td class="unchanged">${content}</td>
</tr>`;
        }
    }

    const unified = `<table class="unified-diff diff">
<colgroup><col class="line-nr"><col class="line-nr"><col class="output"></colgroup>
<thead><tr>
<th class="line-nr" scope="col" title="${yourOutput}"><i class="mdi mdi-18 mdi-file-account" aria-hidden="true"></i><span class="visually-hidden">${lineNumber}</span></th>
<th class="line-nr" scope="col" title="${expectedLabel}"><i class="mdi mdi-18 mdi-file-check" aria-hidden="true"></i><span class="visually-hidden">${lineNumber}</span></th>
<th scope="col"><span class="visually-hidden">${outputLabel}</span></th>
</tr></thead>
<tbody>${unifiedRows}</tbody>
</table>`;

    // -------------------------------------------------------------------------
    // Split table
    // -------------------------------------------------------------------------
    let splitRows = "";
    for (const { expNr, genNr, ins, del, isUnchanged } of processed) {
        let genCell: string;
        let expCell: string;

        if (isUnchanged) {
            // Unchanged rows: prefer delHtml (generated side) as the content for both columns.
            const leftContent = del ?? ins ?? "";
            genCell = `<td class="unchanged">${leftContent}</td>`;
            expCell = `<td class="unchanged">${leftContent}</td>`;
        } else {
            genCell = del !== null ? `<td class="del">${del}</td>` : `<td></td>`;
            expCell = ins !== null ? `<td class="ins">${ins}</td>` : `<td></td>`;
        }

        splitRows += `<tr>
<td class="line-nr">${genNr}</td>
${genCell}
<td class="line-nr">${expNr}</td>
${expCell}
</tr>`;
    }

    const split = `<table class="split-diff diff">
<colgroup><col class="line-nr"><col class="del-output"><col class="line-nr"><col class="ins-output"></colgroup>
<thead><tr>
<th class="line-nr" scope="col" title="${yourOutput}"><i class="mdi mdi-18 mdi-file-account" aria-hidden="true"></i><span class="visually-hidden">${lineNumber}</span></th>
<th scope="col">${yourOutput}</th>
<th class="line-nr" scope="col" title="${expectedLabel}"><i class="mdi mdi-18 mdi-file-check" aria-hidden="true"></i><span class="visually-hidden">${lineNumber}</span></th>
<th scope="col">${expectedLabel}</th>
</tr></thead>
<tbody>${splitRows}</tbody>
</table>`;

    return { split, unified };
}
